// ── 매입 관리 (PINE / SEMECOLON) 페이지 ──────────────────────────────────────
// companyCode prop 으로 회사별 페이지 재사용.
// 권한: CASHFLOW_PURCHASE_<CODE>_MGMT.
// 2026-10-08: 체크칸으로 골라 선택 삭제 (같은 열쇠로 — 7월 전과 같은 기준).
// 2026-11-03: 표에서 바로 쓰기·고치기(노션식)·줄 끝 🗑 — 예전 줄 수정 창(PurchaseEditModal)은 없앴다.
// 2026-11-03 배치(사용자 그림): 제목 아래 [검색 · 초기화] ── 오른쪽 [📤 업로드 ▾](엑셀/CSV·붙여넣기·이력보기).
//   예전 화면 아래 '업로드 이력' 표(UploadSection)는 이력보기 창(BatchHistoryModal)으로, 변경 [이력]은 표 위 줄 오른쪽으로.
//   검색 = 홍보물 관리처럼 업체·비고·비고2 에 글자가 일부만 들어 있어도 그 줄만 보인다(결정 1a). 건수는 "보이는 / 전체".

import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useToast } from '../../components/Toast';
import { useCashflowPurchase } from './cashflowpurchase/useCashflowPurchase';
import UploadMenu from './cashflowpurchase/UploadMenu';
import BatchHistoryModal from './cashflowpurchase/BatchHistoryModal';
import PurchaseTable from './cashflowpurchase/PurchaseTable';
import PurchaseAuditLogModal from './cashflowpurchase/AuditLogModal';
import PastePurchaseModal from './cashflowpurchase/PastePurchaseModal';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle, stdSearchWrap, stdSearchInput, stdSearchIcon, stdOutlineBtn,
} from './adminlayout/designStandard';

const MOBILE_BREAKPOINT = 768;

// 샘이콜론만 이름을 남긴다 — 본사가 아니라 별개 사업자다.
// 본사는 회사코드가 사이트마다 다르므로(파인그룹 PINE / 미팅링크 LINK) 이름을 붙이지 않는다.
const COMPANY_TITLE = {
  SEMECOLON: '샘이콜론 매입 관리',
};
const COMPANY_DESC = {
  SEMECOLON: '샘이콜론 매입 내역을 관리합니다.',
};
const DEFAULT_TITLE = '매입 관리';
const DEFAULT_DESC = '매입 내역을 관리합니다.';

export default function CashflowPurchaseManagement({ companyCode = 'PINE' }) {
  const { showToast } = useToast();
  const code = String(companyCode || 'PINE').toUpperCase();
  const {
    rows, batches, loading,
    create, update, removeMany, upload, pasteRows, rollback, setColor,
  } = useCashflowPurchase({ companyCode: code, showToast });
  const [auditOpen, setAuditOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // 목록이 바뀌면(삭제·묶음 삭제·회사 바꿈) 없어진 줄은 고른 것에서 뺀다
  useEffect(() => {
    setSelectedIds((prev) => {
      const alive = new Set(rows.map((r) => r.id));
      const next = new Set([...prev].filter((id) => alive.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const toggle = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function deleteSelected(picked) {
    const total = picked.reduce((s, r) => s + Number(r.amount || 0), 0);
    const msg = `고른 ${picked.length}건을 삭제할까요? (합계 ${total.toLocaleString('ko-KR')}원)\n\n`
      + '원본 시트에 이 줄들이 남아 있으면 다음 붙여넣기 때 \'신규\'로 다시 들어옵니다.';
    if (!window.confirm(msg)) return;
    if (await removeMany(picked.map((r) => r.id))) setSelectedIds(new Set());
  }
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  // 검색 — 업체·비고·비고2 에 글자가 일부만 들어 있어도(대소문자 무시) 그 줄만.
  //   한글 형태를 맞춰 비교(NFC) — 맥에서 복사해 넣은 줄은 자모가 따로 저장돼(파인그룹 샘이콜론 170줄) 그냥 비교하면 못 찾는다.
  const q = query.trim().normalize('NFC').toLowerCase();
  const shownRows = useMemo(() => (q
    ? rows.filter((r) => [r.vendor, r.note, r.note2].some((v) => String(v || '').normalize('NFC').toLowerCase().includes(q)))
    : rows), [rows, q]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div style={pageStyle}>
      {/* ── 페이지 헤더: 제목/부제 — 직원 관리 표준 ── */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>{COMPANY_TITLE[code] || DEFAULT_TITLE}</h2>
          <div style={stdPageSubtitle}>{COMPANY_DESC[code] || DEFAULT_DESC}</div>
        </div>
        {loading && <span style={{ color: 'var(--text-sub)', fontSize: 13 }}>로딩 중...</span>}
      </div>

      {/* 2줄 = 검색(제목 아래, 홍보물 관리와 같은 표준 검색칸 + 검색어 있을 때만 [초기화]) ── 오른쪽 [📤 업로드 ▾] */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
        <div style={{ ...stdSearchWrap(isMobile), marginBottom: 0, flex: isMobile ? 1 : undefined, width: isMobile ? undefined : 400 }}>
          <Search size={15} style={stdSearchIcon} />
          <input
            style={stdSearchInput}
            placeholder="업체·비고 검색"
            aria-label="업체·비고 검색"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {query && <button style={{ ...stdOutlineBtn, flexShrink: 0 }} onClick={() => setQuery('')}>초기화</button>}
        <div style={{ marginLeft: 'auto' }}>
          <UploadMenu onUpload={upload} onOpenPaste={() => setPasteOpen(true)} onOpenHistory={() => setHistoryOpen(true)} />
        </div>
      </div>

      {/* 표 카드 스타일은 PurchaseTable 내부의 stdTableWrap 이 담당 */}
      <section>
        <PurchaseTable
          rows={shownRows}
          totalCount={q ? rows.length : null}
          onOpenAuditLog={() => setAuditOpen(true)}
          selectedIds={selectedIds}
          onToggle={toggle}
          onDeleteRows={removeMany}
          onCreate={create}
          onUpdate={update}
          onSetColor={setColor}
          notify={showToast}
          onClearSelection={() => setSelectedIds(new Set())}
          onDeleteSelected={deleteSelected}
        />
      </section>

      {auditOpen && (
        <PurchaseAuditLogModal
          companyCode={code}
          title={`${COMPANY_TITLE[code] || DEFAULT_TITLE} 변경 이력`}
          onClose={() => setAuditOpen(false)}
        />
      )}

      <BatchHistoryModal
        open={historyOpen}
        title={`${COMPANY_TITLE[code] || DEFAULT_TITLE} — 업로드 이력`}
        companyCode={code}
        batches={batches}
        onRollback={rollback}
        onClose={() => setHistoryOpen(false)}
      />

      <PastePurchaseModal
        open={pasteOpen}
        title={`${COMPANY_TITLE[code] || DEFAULT_TITLE} — 붙여넣기 추가`}
        existingRows={rows}
        onClose={() => setPasteOpen(false)}
        onSave={pasteRows}
      />
    </div>
  );
}

const pageStyle = { color: 'var(--text)' };
